'use client';

import * as React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SetupMacroStepDefinition } from '@/components/setup/experience/constants';
import { cn } from '@/lib/utils';

interface SetupProgressStripProps {
  /** The steps to render — built per store kind (4 for physical, 3 for digital). */
  steps: SetupMacroStepDefinition[];
  currentIndex: number;
}

export function SetupProgressStrip({ steps, currentIndex }: SetupProgressStripProps) {
  const { t } = useTranslation('setup');

  return (
    <section
      className="border-border bg-card mb-8 shrink-0 rounded-xl border px-6 py-4 shadow-[0_1px_2px_color-mix(in_srgb,var(--foreground)_4%,transparent)] md:px-8 md:py-[1.125rem]"
      aria-label={t('stepNavigation')}
    >
      <div className="flex w-full min-w-0 items-center overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {steps.map((step, index) => {
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div
                className="flex min-w-0 shrink-0 items-center gap-2.5"
                aria-current={isActive ? 'step' : undefined}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[0.8125rem] font-semibold leading-none transition-[border-color,color,background] duration-200',
                    isDone && 'border-primary bg-primary text-primary-foreground',
                    isActive && 'border-primary text-primary bg-card',
                    !isDone && !isActive && 'border-border text-muted-foreground bg-transparent',
                  )}
                  aria-hidden
                >
                  {isDone ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
                </span>
                <p
                  className={cn(
                    'm-0 whitespace-nowrap text-sm font-semibold leading-tight max-[479px]:text-[0.8125rem]',
                    isActive
                      ? 'text-foreground'
                      : isDone
                        ? 'text-[color-mix(in_srgb,var(--muted-foreground)_45%,var(--foreground))]'
                        : 'text-muted-foreground',
                  )}
                >
                  {t(step.labelKey)}
                </p>
              </div>
              {!isLast ? (
                <span
                  className={cn(
                    'bg-border mx-[clamp(0.625rem,2.5vw,2rem)] h-px min-w-[1rem] flex-[1_1_2rem] self-center rounded-[1px] max-[479px]:mx-1 max-[479px]:min-w-[0.5rem] max-[479px]:flex-[0_0_0.75rem]',
                    isDone && 'bg-[color-mix(in_srgb,var(--primary)_38%,var(--border))]',
                  )}
                  aria-hidden
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}
